import { useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";

const PasswordField = ({ label, value, setValue, show, testId }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        required
        minLength={6}
        className="w-full h-10 px-3 pr-10 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
        data-testid={testId}
      />
    </div>
  </div>
);

export default function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to change password";
      toast.error(typeof msg === "string" ? msg : "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="change-password-page">
      <PageHeader title="Change Password" subtitle="Use a strong password you don't use anywhere else" />
      <form onSubmit={submit} className="max-w-xl bg-white border border-gray-200 rounded-sm p-6" data-testid="change-password-form">
        <div className="flex items-center gap-3 pb-4 border-b border-gray-100 mb-5">
          <div className="h-10 w-10 bg-emerald-50 text-emerald-600 border border-emerald-200 rounded-sm flex items-center justify-center">
            <KeyRound size={18} />
          </div>
          <div>
            <div className="font-display text-lg font-bold">Security</div>
            <div className="text-xs text-gray-500">Minimum 6 characters. Include numbers and symbols for best security.</div>
          </div>
          <button type="button" onClick={() => setShow((s) => !s)} className="ml-auto h-9 px-3 border border-gray-200 rounded-sm text-xs inline-flex items-center gap-1.5 hover:bg-gray-50" data-testid="password-toggle-visibility">
            {show ? <EyeOff size={13} /> : <Eye size={13} />} {show ? "Hide" : "Show"}
          </button>
        </div>
        <div className="space-y-4">
          <PasswordField label="Current Password" value={current} setValue={setCurrent} show={show} testId="password-current" />
          <PasswordField label="New Password" value={next} setValue={setNext} show={show} testId="password-new" />
          <PasswordField label="Confirm New Password" value={confirm} setValue={setConfirm} show={show} testId="password-confirm" />
        </div>
        <div className="flex justify-end pt-5 mt-5 border-t border-gray-100">
          <button type="submit" disabled={loading} className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="password-save">
            <KeyRound size={14} /> {loading ? "Updating..." : "Update Password"}
          </button>
        </div>
      </form>
    </div>
  );
}
